import { Injectable } from '@angular/core';
import PouchDB from 'pouchdb';
import upsert from 'pouchdb-upsert';
PouchDB.plugin(upsert);
import { Whirlpool, encoders } from 'whirlpool-hash';
import { FishingEvent } from './fishing-event.model';
import { Gear } from './gear.model';
import { PotGear } from './pot-gear.model';
import { LonglineGear } from './longline-gear.model';
import { PelagicGear } from './pelagic-gear.model';
import { Catch } from './catch.model';
import { OperatorUser } from './operator-user.model';
import { VesselData } from '../vessel-reg/vessel-data.model';
import { LookupItem } from './lookup-item.model';
import { Coordinate } from './coordinate.model';
import { BehaviorSubject, Subject } from 'rxjs';
import { ConflictResolverService } from './conflict-resolver.service';
import { GroundfishDelivery } from './groundfish-delivery.model';
import { Trip } from './trip.model';
import { nanoid } from 'nanoid';
import { HMSDelivery } from './hms-delivery.model';
import { TrawlTrip } from './trawl-trip.model';
import { TrawlDelivery } from './trawl-delivery.model';
import { TrawlSet } from './trawl-set.model';
import { Insights } from './insights.service';


export type StorageResult = {
  success: boolean;
  error?: string;
  data?: any;
};


/**
 * The biggest part of the app, manages all storing, deleting, and updating or user and local data.
 * Sync and migration is handled here as well
 * The database used is PouchDB. 
 * 
 * @see https://pouchdb.com/guides/
 * In short, a NoSQL database consisting of documents which are essentially JSON objects with a required _id and _rev field
 */
@Injectable({
  providedIn: 'root'
})
export class LocalStorageService {

  /**
   * local_storage is the pouchdb instance that stores 'public' documents, there will be one of these for each installation of the app
   */
  static local_storage: any;

  /**
   * user_storage is the pouchdb instance that stores all user data documents - sets, deliveries, gear...
   */
  static user_storage: PouchDB.Database;
  static storage_open: boolean = false;
  /**
   * When retrieving all user data, it occurs within this promise. All components wait on this before initializing.
   */
  static storage_promise: Promise<boolean>;

  /**
   * Sync runs in this promise.
   */
  static sync_promise: Promise<boolean>;

  /**
   * The current access token for the logged in user's session.
   */
  static accessToken: string;

  /**
   * The current refresh token for the logged in user's session.
   */
  static refreshToken: string;

  /**
  * The open trip, or logbook.
  */
  static user_trip: Trip;
  static user_trip$ = new Subject<Trip>();
  /**
  * All user gear
  */
  static user_gear: Gear[];
  static user_gear$ = new Subject<Gear[]>();
  /**
  * Completed logbooks/trips which have been submitted
  */
  static received_logbooks: (GroundfishDelivery | HMSDelivery)[];
  static received_logbooks$ = new Subject<(GroundfishDelivery | HMSDelivery)[]>();
  /**
   * The completed Groundfish delivery currently being edited for re-submission
   */
  static edited_logbook: {GF: GroundfishDelivery, HMS: HMSDelivery};
  static edited_logbook$ = new Subject<{GF: GroundfishDelivery, HMS: HMSDelivery}>();
  /**
  * Permit numbers added/used from delivery page
  */
  static permit_nums: string[];

  /**
  * Buyers added from the delivery page
  */
  static delivery_buyers: string[];

  /**
  * Extracted from and altered by the user_doc in local_storage or user_storage.
  */
  static current_user: OperatorUser;
  static current_user$ = new Subject<OperatorUser>();
  /**
  * Not sure if this is even needed...
  */
  static logged_in: boolean;

  /**
  * Used when going from vessel-reg to login
  */
  static sign_up: boolean = false;

  /**
  * Updated on every sync, the BehaviorSubject counterpart makes it available immediately to the settings page
  */
  static last_sync: Date;
  static last_sync$: BehaviorSubject<Date>;
  static sync_in_progress: boolean = false;
  static sync_in_progress$ = new Subject<boolean>();

  static west_coast_eez: GeoJSON.Polygon;

  static hms_drafts: HMSDelivery[] = [];
  static hms_drafts$: Subject<HMSDelivery[]> = new Subject<HMSDelivery[]>();

  // TRAWL in-memory references
  static user_trawlTrip: TrawlTrip = new TrawlTrip();
  static user_trawlTrip$: Subject<TrawlTrip> = new Subject<TrawlTrip>();

  static trawl_drafts: TrawlDelivery[] = [];
  static trawl_drafts$: Subject<TrawlDelivery[]> = new Subject<TrawlDelivery[]>();

  static received_trawlLogbooks: TrawlDelivery[] = [];
  static received_trawlLogbooks$: Subject<TrawlDelivery[]> = new Subject<TrawlDelivery[]>();

  static edited_trawlLogbook: TrawlDelivery = new TrawlDelivery();
  static edited_trawlLogbook$: Subject<TrawlDelivery> = new Subject<TrawlDelivery>();

  static trawl_motherships: string[] = [];

  static insights: Insights;
  constructor() { }


  /**
   * Closing and cleaning everything up
   */
  static cleanupStorage() {
    LocalStorageService.user_storage.close();
    LocalStorageService.user_gear = [];
    LocalStorageService.user_trip = new Trip();
    LocalStorageService.received_logbooks = [];
    LocalStorageService.permit_nums = [];
    LocalStorageService.delivery_buyers = [];

    //Reset Trawl references
    LocalStorageService.user_trawlTrip = new TrawlTrip();
    LocalStorageService.received_trawlLogbooks = [];
    LocalStorageService.trawl_drafts = [];
    LocalStorageService.trawl_motherships = [];
  }


  /**
   * Sometimes on iOS, the database will be closed by the system unexpectedly during a session.
   * Best workaround as of now is to reopen the database when this happens and retry any failed database operation.
   * @returns Promise resolving when local storage has been opened
   */

  static reopenStorage(): Promise<boolean> {
    return new Promise<boolean>((res, rej) => {
      this.user_storage.info()
        .then((info: any) => res(true))
        .catch((err: any) => {
          this.user_storage.close().finally(() => {
            LocalStorageService.user_storage = new PouchDB('user-storage-' + LocalStorageService.current_user.userName, {skip_setup: true, auto_compaction: true });
            this.user_storage.info().then((info: any) => {
              console.log('Reopened user storage');
              console.log(info);
              res(true);
            })
          })
      });
    })
   

  }


  /**
   * Retrieve login_status, set important variables from retrieved doc.
   * @returns Promise completing when login_status retrieved and variables set
   */
  static openUserAccounts(): Promise<boolean> {
    LocalStorageService.local_storage = new PouchDB('device-local-storage', {auto_compaction: true});
    console.log('Opening user accounts...');
    return new Promise<boolean>((res, rej) => {
      LocalStorageService.local_storage.get('login_status').then((doc: any) => {
        LocalStorageService.logged_in = doc.isLoggedIn;
        LocalStorageService.current_user = doc.isLoggedIn ? OperatorUser.transformAnonymous(doc.user) : new OperatorUser();
        LocalStorageService.accessToken = doc.user.accessToken;
        LocalStorageService.refreshToken = doc.user.refreshToken;
        res(true);
      }).catch(function (err: { name: string }) {
        console.log('Error in open userAccounts')
        console.log(err)
        if (err.name === 'not_found') {
          console.log('Login status document not found. Creating login status document');
          LocalStorageService.local_storage.put({
            _id: 'login_status',
            isLoggedIn: false,
            user: ''
          }).then(() => {
            LocalStorageService.logged_in = false;
            res(true);
          });

        }
      });

    })

  }



  /**
   * Open the local storage. Retrieve all documents of user specific data and store it in the static variables of this class.
   * Whenever called, this operation occurs within the storage_promise variable which resolves on completion.
   */
  static openLocalStorage() {
    //Database preserved. Destroyed only when corresponding menu item selected
    this.storage_open = false;
    LocalStorageService.user_storage = new PouchDB('user-storage-' + LocalStorageService.current_user.userName, { auto_compaction: true });

    this.storage_promise = new Promise<boolean>((resolve, rej) => {
      this.user_storage.allDocs({ include_docs: true }).then((docs: any) => { 

        let docsToUpdate = [];

        /* User doc */
        let userDoc = docs.rows.find((row: any) => row.id == 'user_doc');

        if (userDoc) {
          let user = OperatorUser.transformAnonymous(userDoc.doc.userInfo);
          user.licenseNumber = this.current_user.licenseNumber;
          user.phoneNumber = this.current_user.phoneNumber;
          user.couchDbConnection = this.current_user.couchDbConnection;
          this.current_user = user;     
          userDoc.userInfo = this.current_user;
          docsToUpdate.push({...userDoc.doc});
          this.current_user.accessToken = LocalStorageService.accessToken;
          this.current_user.refreshToken = LocalStorageService.refreshToken;
          this.current_user$.next(this.current_user);
        } else {
          console.log("No user doc");
          this.insights.trackEvent('openLocalStorage', 'No user doc found');
          docsToUpdate.push({"_id": "user_doc", "userInfo":  LocalStorageService.current_user, "lastUpdate": new Date(0)});
        }
        /* *** */

        /* Gear documents */
        let gearListDoc = docs.rows.find((row: any) => row.id == 'user_gear_list');
        let gearDocs = gearListDoc ? docs.rows.filter((row: any) => (gearListDoc.doc.list as string[]).includes(row.id)) : [];

        if (!gearListDoc) docsToUpdate.push({ _id: 'user_gear_list', list: [] });
        this.user_gear = gearDocs.map((item: any) => item.doc.GearDetails.type == 'Pot' ? PotGear.transformAnonymous(item.doc.GearDetails) :
                                                    item.doc.GearDetails.type == "Longline" ? LonglineGear.transformAnonymous(item.doc.GearDetails) :
                                                    item.doc.GearDetails.type == "Pelagic" ? PelagicGear.transformAnonymous(item.doc.GearDetails) :
                                                    Gear.transformAnonymous(item.doc.GearDetails));
        this.user_gear$.next(this.user_gear);
        /* *** */

        /* GF/HMS Trip document */
        let tripDoc = docs.rows.find((row: any) => row.id == 'trip_pending');
        if (tripDoc) {
          if (!tripDoc.doc.lastUpdate) { //Which means this is evaluating to true, that is it must be an empty string
            this.insights.trackEvent('openLocalStorage', 'Trip doc missing update timestamp');
            console.log(tripDoc.doc);
            tripDoc.doc.lastUpdate = new Date(0); //This is possibly being triggered when it shouldn't
            docsToUpdate.push({...tripDoc.doc});
          }

          this.user_trip = new Trip();
          this.user_trip.setValues(tripDoc.doc.trip);
          this.user_trip.groundfishDelivery.sets = this.user_trip.groundfishDelivery.sets.filter(s => this.user_trip.sets.some(s1 => s1.fishingEventID == s.fishingEventID));
          this.insights.trackTrace('Groundfish trip with ' + this.user_trip.groundfishDelivery.sets.length + ' sets');
        }  else {
          this.insights.trackEvent('openLocalStorage', 'Trip document missing');
          this.user_trip = new Trip();
          console.log('Pending trip doc not found. Creating...');
          docsToUpdate.push({_id: 'trip_pending', trip: new Trip(), lastUpdate: new Date(0)});        
        }
        this.user_trip$.next(this.user_trip);
        /* *** */

        /* Received Logbook documents for GF/HMS */
        let logbookList = (docs.rows as any[]).find((row: any) => row.id == 'user_landings_received');
        let receivedLogbooks = logbookList ? docs.rows.filter((row: any) => logbookList.doc.list.includes(row.id)) : [];

        if (!logbookList) {
          console.log('Completed landings list not found. Creating list document');
          docsToUpdate.push({_id: 'user_landings_received', list: []});
        }
        this.received_logbooks = receivedLogbooks.map((item: any) => {
          if (!item.doc?.landing) return;
          let delivery = item.doc.landing.permitType == undefined ? new HMSDelivery() : new GroundfishDelivery();
          delivery.setValues(item.doc.landing);
          return delivery;
        }).sort((a: (GroundfishDelivery | HMSDelivery), b: (GroundfishDelivery | HMSDelivery)) =>
          a.receivedDate.toISOString() < b.receivedDate.toISOString() ? 1 : -1
        );
        this.received_logbooks$.next(this.received_logbooks);
        /* *** */

        /* Edited GF/HMS document */
        let editDoc = docs.rows.find((row: any) => row.id == 'logbook_edited');
        if (editDoc) {
          this.edited_logbook = {GF: new GroundfishDelivery, HMS: new HMSDelivery};
          if (!editDoc.doc.lastUpdate) {
            editDoc.doc.lastUpdate = new Date(0);
            docsToUpdate.push({...editDoc.doc});
          }
          this.edited_logbook.GF.setValues(editDoc.doc.groundFish);
          this.edited_logbook.HMS.setValues(editDoc.doc.HMS);
          
        } else {
          console.log('No edited logbook doc found, creating..');
          docsToUpdate.push({
            _id: 'logbook_edited',
            groundFish: new GroundfishDelivery(),
            HMS: new HMSDelivery(),
            lastUpdate: new Date(0),
          });
          this.edited_logbook = {GF: new GroundfishDelivery, HMS: new HMSDelivery};
        }
        this.edited_logbook$.next(this.edited_logbook);       
        /* *** */
        
        /* Permit numbers */
        let permitDoc = docs.rows.find((row: any) => row.id == 'permit_numbers');
        this.permit_nums = permitDoc ? permitDoc.doc.list : [];
        docsToUpdate.push({_id: 'permit_numbers', list: this.permit_nums});
        /* *** */

        /* Buyers */
        let buyerDoc = docs.rows.find((row: any) => row.id == 'delivery_buyers');
        this.delivery_buyers = buyerDoc ? buyerDoc.doc.list : [];
        docsToUpdate.push({_id: 'delivery_buyers', list: this.delivery_buyers});       
        /* *** */

        /* HMS drafts */
        let hmsDraftList = docs.rows.find((row: any) => row.id == 'hms_drafts');
        let hmsDrafts = hmsDraftList ? docs.rows.filter((row: any) => hmsDraftList.doc.list.includes(row.id)) : [];

        if (!hmsDraftList) {
          console.log('HMS draft list not found. Creating list document');
          docsToUpdate.push({ _id: 'hms_drafts', list: [], lastUpdate: new Date() });
        }

        this.hms_drafts = hmsDrafts.map((item: any) => {
          if (!item.doc || !item.doc.delivery) return;
          let delivery = new HMSDelivery();
          delivery.setValues(item.doc.delivery);
          return delivery;
        });
        this.hms_drafts$.next(this.hms_drafts);
        /* *** */

        /* Trawl trip */
        let trawlTripDoc = docs.rows.find((row: any) => row.id == 'trawl_trip');

        if (trawlTripDoc) {
          if (trawlTripDoc.doc.lastUpdate) {
            trawlTripDoc.doc.lastUpdate = new Date(0);
            docsToUpdate.push({...trawlTripDoc.doc});
          }

          this.user_trawlTrip = new TrawlTrip();
          this.user_trawlTrip.setValues(trawlTripDoc.doc.trip);
          this.user_trawlTrip.trawlDelivery.sets = 
                this.user_trawlTrip.trawlDelivery.sets.filter(tdSet =>
                  this.user_trawlTrip.sets.some(
                    mainSet => mainSet.fishingEventID === tdSet.fishingEventID
                  )
                );  
        } else {
          this.user_trawlTrip = new TrawlTrip();
          console.log('Pending trip doc not found. Creating...');
          docsToUpdate.push({_id: 'trawl_trip', trip: new TrawlTrip(), lastUpdate: new Date(0)});        
        }
        this.user_trawlTrip$.next(this.user_trawlTrip);
        /* *** */

        /* Received Trawl Logbooks */
        let logbookListTrawl = docs.rows.find((row: any) => row.id == 'trawl_landings_received'); 
        let receivedTrawlLogbooks = logbookListTrawl ? docs.rows.filter((row: any) => logbookListTrawl.doc.list.includes(row.id)) : [];

        if (!logbookListTrawl) {
          console.log('Trawl landings received list not found. Creating list document');
          docsToUpdate.push({_id: 'trawl_landings_received', list: []});
        }
        this.received_trawlLogbooks = receivedTrawlLogbooks.map((row: any) => {
          if (!row.doc || !row.doc.landing) return;
          let delivery =  new TrawlDelivery();
          delivery.setValues(row.doc.landing);
          return delivery;
        }).sort((a: TrawlDelivery, b: TrawlDelivery) =>
          a.receivedDate.toISOString() < b.receivedDate.toISOString() ? 1 : -1
        );
        this.received_trawlLogbooks$.next(this.received_trawlLogbooks);
        /* *** */
        
        /* Edited Trawl Logbook */
        let editedTrawlDoc = docs.rows.find((row: any) => row.id == 'trawl_logbook_edited');
        
        if (editedTrawlDoc) {
          this.edited_trawlLogbook = new TrawlDelivery();
          if (!editedTrawlDoc.doc.lastUpdate) {
            editedTrawlDoc.doc.lastUpdate = new Date(0);
            docsToUpdate.push({...editedTrawlDoc.doc});
          }
          this.edited_trawlLogbook.setValues(editedTrawlDoc.doc.trawlDelivery);
          
        } else {
          console.log('No edited trawl logbook doc found, creating..');
          docsToUpdate.push({
            _id: 'trawl_logbook_edited',
            trawlDelivery: new TrawlDelivery(),
            lastUpdate: new Date(0),
          });
          this.edited_trawlLogbook = new TrawlDelivery();
        }
        this.edited_trawlLogbook$.next(this.edited_trawlLogbook);       
        /* *** */
        
        /* Trawl drafts */
        let trawlDraftList = docs.rows.find((row: any) => row.id == 'trawl_drafts');
        if (!trawlDraftList) {
          console.log('Trawl draft list not found. Creating list document');
          docsToUpdate.push({ _id: 'trawl_drafts', list: [], lastUpdate: new Date() });
        } else {
          let trawlDrafts = docs.rows.filter((row: any) => trawlDraftList.doc.list.includes(row.id));
          this.trawl_drafts = trawlDrafts.map((item: any) => {
            const tDelivery = new TrawlDelivery();
            tDelivery.setValues(item.doc.delivery);
            return tDelivery;
          });
          this.trawl_drafts$.next(this.trawl_drafts);
        }
        /* *** */
        
        /* Trawl motherships */
        let trawlMotherships = docs.rows.find((row: any) => row.id == 'mothership_names');
        this.trawl_motherships = trawlMotherships ? trawlMotherships.doc.list : [];
        docsToUpdate.push({_id: 'mothership_names', list: this.trawl_motherships});   
        /* *** */

        /* Local sync date */
        let syncDoc = docs.rows.find((row: any) => row.id == '_local/last_sync');

        if (syncDoc) {
          if (this.last_sync) {
            if (new Date(syncDoc.doc.last_sync).toISOString() > new Date(this.last_sync).toISOString()) this.last_sync = new Date(syncDoc.doc.last_sync);
          } else this.last_sync = new Date(syncDoc.doc.last_sync);
          if(!this.last_sync$) this.last_sync$ = new BehaviorSubject<Date>(this.last_sync);
        } else {
          docsToUpdate.push({_id: '_local/last_sync', last_sync: new Date()});
          this.last_sync = new Date();
          if(!this.last_sync$) this.last_sync$ = new BehaviorSubject<Date>(this.last_sync);
        }

        this.user_storage.bulkDocs(docsToUpdate).then(() => {
          console.log("All user data retrieved");
          this.storage_open = true;
          resolve(true);
        })
      });
    });
  }

  
  /**
   * Hash the given password and compare to value stored in local profile of this user.
   * @param username 
   * @param password 
   * @returns Promise resolving with password hash match
   */
  static verifyLocalProfile(username: string, password: string) {
    return new Promise<boolean>((res, rej) => {
      this.local_storage.get("user-" + username.toLowerCase()).catch((err: any) => {
        if (err.name == 'not_found') res(false);
      }).then((lp: any) => {
        let hash = encoders.toBase64(new Whirlpool().getHash(password).toString());
        res(hash == lp.pwHash);
      });
    });
  }


  /**
   * If offline, retrieve stored lookup data from database here.
   * @returns Promise resolving lookup data
   */
  static getLookupData(): Promise<any> {
    return new Promise((resolve, rej) => {
      this.local_storage.get('lookup_data').then((doc: any) => resolve(doc)).catch((err: any) => {
        console.log('Error storing lookup data');
        if (err.name == 'not_found') {
          console.log('No lookup data doc found, creating...');
          LocalStorageService.local_storage.put({
            _id: 'lookup_data',
            data: [],
            trawlData: []
          }).then(() => resolve([]));
        }
      });
    });
  }


  /*
    Storage methods - Storage operations on fishing events, gears, and completed deliveries must also make a corresponding update to the matching list document
  */


  /**
   * Every user that logs in or signs up on a device will be stored as a user profile document.
   * Use the whirlpool hashing scheme
   * @param user 
   * @param password 
   * @returns Promise resolving on storage success/failure
   * 
   * @see
   * https://en.wikipedia.org/wiki/Whirlpool_(hash_function)
   */
  static storeUser(user: OperatorUser, password: string): Promise<boolean> {
    let hash = encoders.toBase64(new Whirlpool().getHash(password).toString());
    return new Promise<boolean>((res, rej) => {
      this.local_storage.put(
        {
          "_id": "user-" + user.userName.toLowerCase(),
          "userInfo": user,
          "pwHash": hash
        }
      ).then((result: any) => {
        res(true);
      }).catch((err: any) => {
        this.insights.trackException(err);
        console.log("Error on store user:");
        console.log(err);
        res(false);
      });
    });
  }


  /**
  * This method is called on creation of a new set to store it.
  * @param event the fishing event/set to store
  * @returns Promise resolving on storage success/failure
  */
  static storeFishingEvent(
    event: FishingEvent | TrawlSet,
    fishery: 'default' | 'trawl' = 'default',
    isNew: boolean = true
  ): Promise<StorageResult> {

    if (isNew) {
      //Auto numbering of sets, one plus the current highest numbered set
      let autoNum = (fishery == 'trawl' ? this.user_trawlTrip.sets : this.user_trip.sets).map(s => s.setNumber).reduce((max, s) => s > max ? s : max, '0');
      event.setNumber = (+autoNum + 1).toString();
      event.fishingEventID = "set-" + nanoid();
    }

    // Add the event to the correct in-memory trip 
    if (fishery === 'trawl') {
      this.user_trawlTrip.sets.push(event as TrawlSet);
    } else {
      this.user_trip.sets.push(event as FishingEvent);
    }

    // Save to the correct doc
    const docId = this.getDocId('trip', fishery);
    return new Promise<any>((res) => {
      this.reopenStorage().finally(() => {
        this.user_storage.upsert(docId, (setDoc: any) => {
          setDoc.trip = fishery === 'trawl' ? this.user_trawlTrip : this.user_trip;
          setDoc.lastUpdate = new Date();
          return setDoc;
        }).then(() => res({success: true, data: event.fishingEventID}))
        .catch(err => res({success: false, error: err}));
      });
      
    });
  }



  /**
   * Store a gear, remember to select the proper gear type: Pot, longline, pelagic.
   * @param gear The gear object to store
   * @returns Promise resolving on storage success/failure
   */
  static storeGear(gear: Gear): Promise<boolean> {
    this.user_gear.push(gear);

    // Three different types of gear
    return new Promise<boolean>((res, rej) => {

      var json = {
        "_id": gear.gearID,
        "GearDetails": gear.fishery == 'HMS' ? gear : gear.type == 'Pot' ? gear as PotGear : gear.type[0] == 'L' ? gear as LonglineGear : gear as PelagicGear,
        "lastUpdate": new Date()
      };

      this.user_storage.put(json).then((result: any) => {
        this.user_storage.get('user_gear_list').then((doc: any) => {
          doc.list.push(gear.gearID);
          this.user_storage.put(doc).then((r: any) => {
            res(true);
          });
        });
      });
    });
  }

  /**
   * 
   * @param data A buyer, mothership name, or permit number
   * @param docId The document we are adding the data to
   * @returns Promise on completion of the upsert operation
   */
  static addAuxiliaryData(data: string, docId: string): Promise<StorageResult> {
    return new Promise<StorageResult>((res) => {
      this.user_storage.upsert(docId, (doc: any) => {
        if (!doc.list) doc.list = [];
        doc.list.push(data);
        switch (docId) {
          case 'delivery_buyers':
            this.delivery_buyers.push(data);
            break;
          case 'permit_numbers':
            this.permit_nums.push(data);
            break;
          case 'mothership_names':
            this.trawl_motherships.push(data);
            break;
        }
        return doc;
      }).then(() => res({success: true})).catch(err => res({success: false, error: err}));
    });
  }


  /**
   * Anytime lookup data is retrieved from the server, store it in case it needs to be pulled while offline.
   * @param data Lookup data retrieved from server
   */
  static storeLookupData(gfData: any[], trawlData: any[]) {
    this.local_storage.get('lookup_data').then((doc: any) => {
      doc.data = gfData;
      doc.trawlData = trawlData;
      this.local_storage.put(doc);
    }).catch((err: any) => {
      console.log('Error storing lookup data');
      if (err.name == 'not_found') {
        console.log('No lookup data doc found, creating...');
        LocalStorageService.local_storage.put({
          _id: 'lookup_data',
          data: gfData,
          trawlData: trawlData
        });
      }
    });
  }


  /**
   * Lookup data for species may have changed
   * @param data 
   */
  static checkUserSpecies(data: LookupItem[]) {
    this.current_user.favoriteSpecies.forEach((sp, i) => {
      this.current_user.favoriteSpecies[i] = data.find(v => v.name.trim().toLowerCase() == sp.name.trim().toLowerCase()) ?? this.current_user.favoriteSpecies[i];
    })
  }

  /*
    Deletion methods
    Each method also needs to make a corresponding update to the corresponding list document
  */


  /**
   * Removing a set means its id needs to be removed from user_sets_pending document.
   * The set might be attached to the current delivery hence an update may be needed there as well.
   * @param id Document id of fishing event to delete
   * @returns Promise resolving on storage success/failure
   */
  static deleteFishingEvent(
    id: string,
    isOriginalSet: boolean = false,
    fishery: 'GF' | 'HMS' | 'trawl'
  ): Promise<boolean> {
    let set: FishingEvent | TrawlSet | undefined;

    let edited = fishery === 'trawl' ? this.edited_trawlLogbook : fishery == 'GF' ? this.edited_logbook.GF : this.edited_logbook.HMS;
    let trip = fishery === 'trawl' ? this.user_trawlTrip : this.user_trip;

    set = isOriginalSet ? edited.sets.find(s => s.fishingEventID === id) : undefined;
    const idx = isOriginalSet ? -1 : trip.sets.findIndex(fe => fe.fishingEventID === id);
    set = set ?? trip.sets.splice(idx, 1)[0];

    return new Promise<boolean>((resolve) => {
      // 1) Check if set is undefined
      if (!set) {
        return resolve(false);
      }

      // 2) If you need .fishery, do an appropriate check
      //    If TrawlSet doesn't have .fishery, handle Trawl logic separately:
      const tripDocId = this.getDocId('trip', fishery != 'trawl' ? 'default' : 'trawl');
      const editDocId = this.getDocId('logbook_edited', fishery != 'trawl' ? 'default' : 'trawl');

      this.user_storage.allDocs({ keys: [tripDocId, editDocId], include_docs: true }).then((docs: any) => {
        let tripPend = docs.rows[0].doc;
        let lbEdit = docs.rows[1].doc;

        if (fishery === 'trawl') {
          // Trawl logic, TrawlSet doesn't have set.fishery
          tripPend.trip.sets = tripPend.trip.sets.filter((ts: TrawlSet) => ts.fishingEventID !== id);
          lbEdit.trawlDelivery.sets = lbEdit.trawlDelivery.sets.filter((ts: TrawlSet) => ts.fishingEventID !== id);
        } else {
          // Default logic, now we know set is a FishingEvent with a .fishery
          if ((set as FishingEvent).fishery === 'Groundfish') {
            this.user_trip.groundfishDelivery.sets =
              this.user_trip.groundfishDelivery.sets.filter(s => s.fishingEventID !== id);
            this.edited_logbook.GF.sets =
              this.edited_logbook.GF.sets.filter(s => s.fishingEventID !== id);
            lbEdit.groundFish = this.edited_logbook.GF;
          } else {
            this.user_trip.hmsDelivery.sets =
              this.user_trip.hmsDelivery.sets.filter(s => s.fishingEventID !== id);
            this.edited_logbook.HMS.sets =
              this.edited_logbook.HMS.sets.filter(s => s.fishingEventID !== id);
            lbEdit.HMS = this.edited_logbook.HMS;
          }
          tripPend.trip = this.user_trip;
        }

        tripPend.lastUpdate = new Date();
        lbEdit.lastUpdate = new Date();

        this.user_storage.bulkDocs([tripPend, lbEdit]).then(() => {
          resolve(true);
        }).catch(() => resolve(false));
      }).catch(() => resolve(false));
    });
  }


  /**
   * If a gear is on a pending set, deleting it will not be allowed
   * Remove the gear document and update the gear list document to reflect its removal.
   * @param id Document id of gear to delete
   * @returns Promise resolving on completion of deletion
   */
  static deleteGear(id: string) {
    return new Promise<boolean>((resolve, rej) => {
      this.user_storage.get(id).then(function(doc: any) {
        LocalStorageService.user_storage.remove(doc);
      }).then(function (result: any) {
        LocalStorageService.user_storage.get('user_gear_list').then((list_doc: any) => {
          list_doc.list.splice(list_doc.list.indexOf(id), 1);
          LocalStorageService.user_storage.put(list_doc).then(() => {
            resolve(true);
          });
        });
      }).catch(function (err: any) {
        console.log(err);
        resolve(false);
      });
    });

  }



  /*
    Update methods
  */

  /**
   * After landing submission need to: delete the landing's attached sets, add the landing to the list of completed deliveries, 
   * update the pending_sets document, and reset the pending landing.
   * @param landing The completed landing that has been submitted and received by the server
   * @returns Promise resolving on completion of all database operations
   */
  static afterDeliveryReceived(
    landing: GroundfishDelivery | HMSDelivery | TrawlDelivery,
    fishery: 'default' | 'trawl' = 'default',
    isDraft: boolean = false
  ) {
    const landingsDocId = this.getDocId('landings_received', fishery);
    const tripDocId = this.getDocId('trip', fishery);
    const editDocId = this.getDocId('logbook_edited', fishery);

    return Promise.all([
      new Promise<StorageResult>((resolve) => {
        if (!isDraft || fishery != 'default') {
          resolve({success: true});    
          return;
        }   
        this.user_storage.allDocs({include_docs: true, keys: ['hms_drafts', `hms_draft_${landing.localLandingGUID}`, landingsDocId]}).then((drafts: any) => {
          console.log(drafts);
          let hmsDrafts = drafts.rows[0].doc;
          hmsDrafts.list = hmsDrafts.list.filter((d: string) => d != `hms_draft_${landing.localLandingGUID}`);
          hmsDrafts.lastUpdate = new Date();
          drafts.rows[1].doc._deleted = true;
          drafts.rows[2].doc.list.push(landing.logbookId);
          drafts.rows[2].doc.lastUpdate = new Date();
          const landingDoc = {
            _id: landing.logbookId,
            landing: landing
          };
          this.user_storage.bulkDocs([hmsDrafts, drafts.rows[1].doc, drafts.rows[2].doc, landingDoc]).then(() => resolve({success: true}));
        });
      }),

      new Promise<boolean>((resolve) => {
        if (!isDraft || fishery != 'trawl') {
          resolve(true);    
          return;
        }   
        this.user_storage.allDocs({include_docs: true, keys: ['trawl_drafts', `trawl_draft_${landing.localLandingGUID}`, landingsDocId]}).then((drafts: any) => {
          let trawlDrafts = drafts.rows[0].doc;
          trawlDrafts.list = trawlDrafts.list.filter((d: string) => d != `trawl_draft_${landing.localLandingGUID}`);
          trawlDrafts.lastUpdate = new Date();
          drafts.rows[1].doc._deleted = true;
          drafts.rows[2].doc.list.push(landing.logbookId);
          drafts.rows[2].doc.lastUpdate = new Date();
          const landingDoc = {
            _id: landing.logbookId,
            landing: landing
          };
          this.user_storage.bulkDocs([trawlDrafts, drafts.rows[1].doc, drafts.rows[2].doc, landingDoc]).then(() => resolve(true));
        });
      }),

      new Promise<boolean>((resolve) => {
        if (isDraft) {
          resolve(true);    
          return;
        }   
        this.user_storage.allDocs({
          include_docs: true,
          keys: [landingsDocId, tripDocId, editDocId]
        }).then((docs: any) => {
  
          let rlDoc = docs.rows[0].doc;
          let ptDoc = docs.rows[1].doc;
          let geDoc = docs.rows[2].doc;
  
          //Add to received landings
          if (!rlDoc.list) rlDoc.list = [];
          rlDoc.list.push(landing.logbookId);
  
          if (fishery === 'trawl' && landing instanceof TrawlDelivery) {
            //Remove sets from the user_trawlTrip
            ptDoc.trip.sets = ptDoc.trip.sets.filter((st: TrawlSet) =>
              !landing.sets.some(lst => lst.fishingEventID === st.fishingEventID)
            );
            ptDoc.trip.trawlDelivery = new TrawlDelivery();
  
            geDoc.trawlDelivery.sets = geDoc.trawlDelivery.sets.filter((eds: TrawlSet) =>
              !landing.sets.some(ls => ls.fishingEventID === eds.fishingEventID)
            );
          } else {
            // Original default logic
            if (landing instanceof GroundfishDelivery) {
              ptDoc.trip.groundfishDelivery = new GroundfishDelivery();
            } else {
              ptDoc.trip.hmsDelivery = new HMSDelivery();
            }
            this.user_trip.sets = this.user_trip.sets.filter(
              st => !landing.sets.some(lst => lst.fishingEventID == st.fishingEventID));
            ptDoc.trip.sets = this.user_trip.sets;
  
            this.edited_logbook.GF.sets = this.edited_logbook.GF.sets.filter(
              s => landing.sets.findIndex(ls => ls.fishingEventID == s.fishingEventID) == -1
            );
            this.edited_logbook.HMS.sets = this.edited_logbook.HMS.sets.filter(
              s => landing.sets.findIndex(ls => ls.fishingEventID == s.fishingEventID) == -1
            );
            geDoc.groundFish = this.edited_logbook.GF;
            geDoc.HMS = this.edited_logbook.HMS;
          }
  
          ptDoc.lastUpdate = new Date();
          geDoc.lastUpdate = new Date();
  
          const landingDoc = {
            _id: landing.logbookId,
            landing: landing
          };
  
          this.user_storage.bulkDocs([rlDoc, ptDoc, geDoc, landingDoc]).then(() => {
            if (fishery === 'trawl' && landing instanceof TrawlDelivery) {
              this.received_trawlLogbooks.push(landing);
              this.user_trawlTrip.sets = ptDoc.trip.sets;
              this.user_trawlTrip.trawlDelivery = new TrawlDelivery();
            } else {
              this.received_logbooks.push(landing as GroundfishDelivery | HMSDelivery);
              if (landing instanceof GroundfishDelivery) {
                this.user_trip.groundfishDelivery = new GroundfishDelivery();
              } else {
                this.user_trip.hmsDelivery = new HMSDelivery();
              }
            }
            resolve(true);
          }).catch(() => resolve(false));
        }).catch(() => resolve(false));
      })
    ]);
  }


  static saveHMSDraftTrip(hmsTrip: HMSDelivery): Promise<StorageResult> {
    return new Promise<StorageResult>((resolve) => {     
      this.user_storage.allDocs({
        include_docs: true,
        keys: ['trip_pending', 'logbook_edited', 'hms_drafts']
      }).then((docs: any) => {

        const tripDoc = docs.rows[0].doc;
        const editDoc = docs.rows[1].doc;
        let draftsDoc = docs.rows[2].doc;

        // add draft id
        if (!draftsDoc.list) draftsDoc.list = [];
        draftsDoc.list.push(`hms_draft_${hmsTrip.localLandingGUID}`);
        draftsDoc.lastUpdate = new Date();

        // strip sets from pending + edit docs
        tripDoc.trip.hmsDelivery = new HMSDelivery();
        tripDoc.trip.sets = tripDoc.trip.sets
          .filter((set: FishingEvent) =>
            hmsTrip.sets.findIndex(s => s.fishingEventID === set.fishingEventID) === -1);

        editDoc.HMS.sets = editDoc.HMS.sets
          .filter((set: FishingEvent) =>
            hmsTrip.sets.findIndex(s => s.fishingEventID === set.fishingEventID) === -1);

        tripDoc.lastUpdate = editDoc.lastUpdate = new Date();
        editDoc.lastUpdate = new Date();

        const draftDoc = {
          _id: `hms_draft_${hmsTrip.localLandingGUID}`,
          delivery: hmsTrip,
          lastUpdate: new Date()
        };

        this.user_storage.bulkDocs([draftsDoc, tripDoc, editDoc, draftDoc]).then(() => {
          // update in‑memory list
          this.hms_drafts.push(hmsTrip);
          this.hms_drafts$.next(this.hms_drafts);
          resolve({success: true});
        }).catch((err) => resolve({success: false, error: err}));
      });
    });
  }
  


  static afterHMSDraftReceived(draftIndex: number): Promise<StorageResult> {
    return new Promise<StorageResult>((resolve) => {
  
      this.user_storage.get('hms_drafts').then((draftsDoc: any) => {
  
        const keys = [...draftsDoc.list, 'trip_pending', 'logbook_edited'];
        this.user_storage.allDocs({ include_docs: true, keys })
          .then((docs: any) => {
  
            const submittedDraftDoc = docs.rows.splice(draftIndex, 1);
            const submittedSets     = (submittedDraftDoc.doc.delivery as HMSDelivery).sets;

            // keep sets that aren’t in the submitted draft
            docs.rows.forEach((row: any) => {
              (row.doc.delivery as HMSDelivery).sets =
                (row.doc.delivery as HMSDelivery).sets.filter(
                  s => !submittedSets.some(ss => ss.fishingEventID === s.fishingEventID));
              row.doc.lastUpdate = new Date();
            });
  
            // tidy pending + edited trip docs (last two rows)
            const tripDoc = docs.rows[docs.rows.length - 2].doc;
            const editDoc = docs.rows[docs.rows.length - 1].doc;
  
            tripDoc.trip.sets = tripDoc.trip.sets.filter(
              (set: FishingEvent) =>
                !submittedSets.some(ss => ss.fishingEventID === set.fishingEventID));
  
            editDoc.HMS.sets = editDoc.HMS.sets.filter(
              (set: FishingEvent) =>
                !submittedSets.some(ss => ss.fishingEventID === set.fishingEventID));
  
            tripDoc.lastUpdate = editDoc.lastUpdate = new Date();
  
            // remove from list & mark draft doc deleted
            draftsDoc.list.splice(draftIndex, 1);
            submittedDraftDoc._deleted = true;
  
            const docsToSave = docs.rows.map((r: any) => r.doc)
              .concat([tripDoc, editDoc, draftsDoc, submittedDraftDoc]);
  
            this.user_storage.bulkDocs(docsToSave).then(() => {
              // update in‑memory list
              this.hms_drafts.splice(draftIndex, 1);
              this.hms_drafts$.next(this.hms_drafts);
              resolve({success: true});
            }).catch((err) => resolve({success: false, error: err}));
          });
      });
    });
  }
  


  static saveTrawlDraftTrip(trawlTrip: TrawlDelivery): Promise<StorageResult> {
    return new Promise<StorageResult>((resolve) => {
      this.user_storage.allDocs({
        include_docs: true,
        keys: ['trawl_drafts', 'trawl_trip', 'trawl_logbook_edited']
      }).then((docs: any) => {

        let draftsDoc = docs.rows[0].doc;
        let tripDoc = docs.rows[1].doc;
        let editDoc = docs.rows[2].doc;

        //Add to draft trips
        if (!draftsDoc.list) draftsDoc.list = [];
        draftsDoc.list.push(`trawl_draft_${trawlTrip.localLandingGUID}`);
        draftsDoc.lastUpdate = new Date();

        tripDoc.trip.trawlDelivery = new TrawlDelivery(); 
        tripDoc.trip.sets = tripDoc.trip.sets
        .filter((set: TrawlSet) => 
          trawlTrip.sets.findIndex((s: TrawlSet) => s.fishingEventID == set.fishingEventID) == -1);

        editDoc.trawlDelivery.sets = editDoc.trawlDelivery.sets.
        filter((set: TrawlSet) => 
          trawlTrip.sets.findIndex((s: TrawlSet) => s.fishingEventID == set.fishingEventID) == -1);

        tripDoc.lastUpdate = new Date();
        editDoc.lastUpdate = new Date();

        const draftDoc = {
          _id: `trawl_draft_${trawlTrip.localLandingGUID}`,
          delivery: trawlTrip,
          lastUpdate: new Date()
        };

        this.user_storage.bulkDocs([draftsDoc, tripDoc, editDoc, draftDoc])
        .then(() => {
          this.trawl_drafts.push(trawlTrip);
          this.trawl_drafts$.next(this.trawl_drafts);
          resolve({success: true});
        })
        .catch((err) => resolve({success: false, error: err}));
      })
    });
  }


  static afterTrawlDraftReceived(draftIndex: number): Promise<StorageResult> {
    return new Promise<StorageResult>((resolve) => {
      //Remove draft from drafts list
      this.user_storage.get('trawl_drafts').then((draftsDoc: any) => {
        this.user_storage.allDocs({keys: (draftsDoc.list as any[]).concat(['trawl_trip', 'trawl_logbook_edited']), include_docs: true}).then((drafts: any) => {
          let submitted_draft = drafts.rows.splice(draftIndex, 1);
          let draft_trips = (drafts.rows as any[]).slice(0, drafts.rows.length - 2);
          let tripDoc = drafts.rows[drafts.rows.length - 2].doc;
          let editDoc = drafts.rows[drafts.rows.length - 1].doc;

          draft_trips.forEach((draft: any) => {
            (draft.doc.delivery as TrawlDelivery).sets = (draft.delivery as TrawlDelivery).sets.filter(set => 
              submitted_draft.doc.delivery.sets.findIndex((s: TrawlSet) => s.fishingEventID == set.fishingEventID) == -1);
            draft.doc.lastUpdate = new Date();
          });

          //Remove all sets from tripDoc and editDoc that are in submitted_draft
          tripDoc.trip.sets = tripDoc.trip.sets.filter((set: TrawlSet) => submitted_draft.doc.delivery.sets.findIndex((s: TrawlSet) => s.fishingEventID == set.fishingEventID) == -1);
          editDoc.trawlDelivery.sets = editDoc.trawlDelivery.sets.filter((set: TrawlSet) => submitted_draft.doc.delivery.sets.findIndex((s: TrawlSet) => s.fishingEventID == set.fishingEventID) == -1);

          tripDoc.lastUpdate = new Date();
          editDoc.lastUpdate = new Date();

          draftsDoc.list.splice(draftIndex, 1);
          submitted_draft.doc._deleted = true;

          this.user_storage.bulkDocs((drafts.rows as any[]).map(r => r.doc).concat([tripDoc, editDoc, draftsDoc, submitted_draft.doc]))
          .then(() => resolve({success: true})).catch((err) => resolve({success: false, error: err}));     
        })
      });

      
      //Remove all sets from current trawl trip or other draft trips or edited trawl trip
    });
  }

  /**
   * After submitting an edited landing, need to: delete the landing's attached sets that were not part of the original submission,
   * re-put the received landing doc, update the pending_sets, and remove any sets from the pending landing that were part of this re-submission
   * @param landing The edited landing that has been re-submitted and received by the server
   * @returns Promise resolving on completion of all database operations
   */
  static afterLogbookResubmission(
    landing: GroundfishDelivery | HMSDelivery | TrawlDelivery,
    fishery: 'default' | 'trawl' = 'default'
  ) {
    const docKeys = [
      landing.logbookId,
      this.getDocId('logbook_edited', fishery),
      this.getDocId('trip', fishery)
    ];


    return new Promise<StorageResult>((resolve) => {
      this.user_storage.allDocs({ include_docs: true, keys: docKeys }).then((docs: any) => {
        let landingDoc = docs.rows[0].doc; // existing doc
        let editDoc = docs.rows[1].doc;
        let tripDoc = docs.rows[2].doc;

        landingDoc.landing = landing;
        landingDoc.lastUpdate = new Date();

        if (fishery === 'trawl' && landing instanceof TrawlDelivery) {
          //Reset edited doc
          editDoc.trawlDelivery = new TrawlDelivery();
          this.edited_trawlLogbook = new TrawlDelivery();

          tripDoc.trip.sets = tripDoc.trip.sets.filter((set: TrawlSet) =>
            !landing.sets.some(ls => ls.fishingEventID === set.fishingEventID)
          );
          tripDoc.trip.trawlDelivery.sets = tripDoc.trip.trawlDelivery.sets.filter((st: TrawlSet) =>
            !landing.sets.some(ls => ls.fishingEventID === st.fishingEventID)
          );
        } else {
          // Original GF/HMS logic
          if (landing instanceof GroundfishDelivery) {
            editDoc.groundFish = new GroundfishDelivery();
            this.edited_logbook.GF = new GroundfishDelivery();
          } else {
            editDoc.HMS = new HMSDelivery();
            this.edited_logbook.HMS = new HMSDelivery();
          }
          this.user_trip.sets = this.user_trip.sets.filter(
            set => !landing.sets.some(fe => fe.fishingEventID === set.fishingEventID)
          );
          this.user_trip.groundfishDelivery.sets = this.user_trip.groundfishDelivery.sets.filter(
            set => !landing.sets.some(fe => fe.fishingEventID === set.fishingEventID)
          );
          this.user_trip.hmsDelivery.sets = this.user_trip.hmsDelivery.sets.filter(
            set => !landing.sets.some(fe => fe.fishingEventID === set.fishingEventID)
          );
          tripDoc.lastUpdate = new Date();
          tripDoc.trip = this.user_trip;
        }

        editDoc.lastUpdate = new Date();
        tripDoc.lastUpdate = new Date();

        this.user_storage.bulkDocs([landingDoc, editDoc, tripDoc])
        .then(() => resolve({success: true}))
        .catch((err: any) => resolve({success: false, error: err}));
      }).catch((err: any) => resolve({success: false, error: err}));
    });
  }



  /**
   * If the given user does not have a local profile, this method will create one and store it.
   * Checks must also be made for changed passwords, the user doc in local_storage will have the correct password.
   * The user profile stored in user_storage will need to be updated if it differs.
   * @param data Server response from login/password reset containing all user information
   * @param password Password of user whose info we are looking for
   * @returns Promise resolving on completion of all database operations
   */
  static checkForUserInfo(data: any, password: string) {
    console.log("checking for user info");
    return new Promise<boolean>((res, rej) => {
      this.local_storage.get('user-' + String(data.userName).toLowerCase()).then((doc: any) => {
        //Has the user changed their password on a different device?
        if (doc.userInfo == null) {
          console.log("Replace placeholder");
          let user = new OperatorUser();
          user.setupNewUser(data);
          doc.userInfo = user;
          this.local_storage.put(doc).then((result: any) => {
            res(true);
          }).catch((err: any) => res(false));
        } else {
          //User has been found but passwords don't match
          let hash = encoders.toBase64(new Whirlpool().getHash(password).toString());
          if (hash != doc.pwHash) {
            doc.pwHash = hash;
            this.local_storage.put(doc);
          }
          res(true)
        }
      }).catch((err: any) => {
        if (err.name == 'not_found') {
          console.log('User not found on this device, creating user...');
          let user = new OperatorUser();
          user.setupNewUser(data);
          this.storeUser(user, password).then(doc => {
            res(true);
          });
        } else {
          console.log(err);
          res(false);
        }
      })
    });
  }


  /**
   * Given the list of vessels, either from sign up or updates on settings page, store them.
   * If from sign-up, we store the data in local_storage and copy over to user_storage when initializing the new user.
   * After sign-up, further updates to local_storage document not needed.
   * @param username Username
   * @param vessels New list of vessels to store
   * @param isSignUp Coming from the sign-up process? Or from settings page?
   * @returns Promise resolving on completion of all database operations
   */
  static updateUserVessels(username: string, vessels: VesselData[], isSignUp = false): Promise<boolean> {

    return new Promise<boolean>((resolve, reject) => {
      let db = isSignUp ? this.local_storage : this.user_storage;
      let docName = isSignUp ? 'user-' + username.toLowerCase() : 'user_doc';
      db.upsert(docName, (doc: any) => {
        doc.userInfo.operatedVessels = vessels;
        if (!isSignUp) doc.lastUpdate = new Date();
        return doc;
      }).then(() => resolve(true))
      .catch((err: any) => {
        console.log('Error storing user vessels');
        console.log(err);
      })
    })

  }


  /**
   * Get list of species and store them.
   * @param species The species to store.
   * @returns Promise resolving on completion of all database operations
   */
  static updateUserSpecies(species: LookupItem[]): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.user_storage.get('user_doc').then((doc: any) => {
        doc.userInfo.favoriteSpecies = species;
        doc.lastUpdate = new Date();
        this.user_storage.put(doc);
        resolve(true);
      }).catch((err: any) => {
        console.log('Error storing user favorite species.');
        console.log(err);
      })
    })
  }


  /**
   * When logging in or out, need to update the login_status document to reflect that.
   * login_status doc stores a boolean which is true if a user is currently logged in, and the logged in user itself.
   * @param username Username and hence user document id
   * @returns Promise resolving on completion of all database operations
   */
  static setLoginStatus(username: string) {
    //Set current user and login status
    console.log('Setting login status');
    return new Promise<boolean>((res, rej) => {
      console.log('user-' + username.toLowerCase());
      this.local_storage.get('user-' + username.toLowerCase()).then((doc: any) => {
        this.current_user = OperatorUser.transformAnonymous(doc.userInfo);
        this.logged_in = true;
        this.local_storage.upsert('login_status', (status_doc: any) => {
          status_doc.user = this.current_user;
          status_doc.isLoggedIn = true;
          return status_doc;
        }).then(() => res(true)).catch((err: any) => {;
          console.log('Error on setLoginStatus:');
          console.log(err);
          res(false);
        });
      });
    });
  }


  static updateTrip(fishery: 'default' | 'trawl' = 'default'): Promise<StorageResult> {
    //Pick correct doc
    const docId = this.getDocId('trip', fishery);
    
    return new Promise<StorageResult>((resolve, reject) => {
      this.reopenStorage().finally(() => {
        this.user_storage.upsert(docId, (tripDoc: any) => {
          tripDoc.trip = fishery === 'trawl' ? this.user_trawlTrip : this.user_trip;
          tripDoc.lastUpdate = new Date();
          return tripDoc;
        }).then(() => resolve({success: true}))
        .catch((err: any) => resolve({success: false, error: err}));
      });      
    });
  }

  /**
   * As with sets, any update to the current delivery calls this method to update the pending_landing document.
   * @param landing The current updated landing
   * @returns Promise resolving on completion of all database operations
   */
  static updateLanding(
    landing: GroundfishDelivery | HMSDelivery | TrawlDelivery,
    fishery: 'default' | 'trawl' = 'default',
    isDraft: boolean = false
  ): Promise<StorageResult> {

    // If there's a logbookId, we update 'logbook_edited', otherwise 'trip'
    const docId = landing.logbookId
      ? this.getDocId('logbook_edited', fishery)
      : isDraft ? (fishery === 'trawl' ? `trawl_draft_${landing.localLandingGUID}`
      : `hms_draft_${landing.localLandingGUID}`)
      : this.getDocId('trip', fishery);

    return new Promise<StorageResult>((res) => {
      this.reopenStorage().finally(() => {
        this.user_storage.upsert(docId, (landDoc: any) => {
          if (fishery === 'trawl' && landing instanceof TrawlDelivery) {
            if (landing.logbookId) landDoc.trawlDelivery = landing;
            else if (isDraft) landDoc.delivery = landing;
            else landDoc.trip.trawlDelivery = landing;       
          } else {
            // existing GF/HMS logic
            if (landing.logbookId) landDoc[landing instanceof GroundfishDelivery ? "groundFish" : "HMS"] = landing;
            else if (isDraft && landing instanceof HMSDelivery) landDoc.delivery = landing;
            else landDoc.trip[landing instanceof GroundfishDelivery ? "groundfishDelivery" : "hmsDelivery"] = landing;           
          }

          landDoc.lastUpdate = new Date();
        
          // update in-memory references
          if (fishery === 'trawl' && landing instanceof TrawlDelivery) {
            
            if (landing.logbookId) {
              LocalStorageService.edited_trawlLogbook = landing;
            } else if (isDraft) {
              let ind = LocalStorageService.trawl_drafts.findIndex(td => td.localLandingGUID == landing.localLandingGUID);
              LocalStorageService.trawl_drafts[ind] = landing;
            } else {    
              LocalStorageService.user_trawlTrip.trawlDelivery = landing;
            }

          } else {

            if (landing.logbookId) {
              if (landing instanceof GroundfishDelivery) LocalStorageService.edited_logbook.GF = landing;
              else if (landing instanceof HMSDelivery) LocalStorageService.edited_logbook.HMS = landing;
            } else if (isDraft && landing instanceof HMSDelivery) {
              const idx = LocalStorageService.hms_drafts
                .findIndex(d => d.localLandingGUID === landing.localLandingGUID);
              if (idx > -1) {
                LocalStorageService.hms_drafts[idx] = landing;
                LocalStorageService.hms_drafts$.next(LocalStorageService.hms_drafts);
              }
          
            } else {
              if (landing instanceof HMSDelivery) LocalStorageService.user_trip.hmsDelivery = landing;
              else if (landing instanceof GroundfishDelivery) LocalStorageService.user_trip.groundfishDelivery = landing;
            }
            
          }
          return landDoc;
        }).then(() => res({success: true})).catch((err) => res({success: false, error: err}));
      });
      
    });
  }


  static editExistingGroundfishLanding(logbookId: string) {
    return new Promise<StorageResult>((res, rej) => {
      this.user_storage.get(logbookId).then((geEditDoc: any) => {
        this.edited_logbook.GF = new GroundfishDelivery();
        this.edited_logbook.GF.setValues(geEditDoc.landing);
       this.user_storage.upsert('logbook_edited', (landEdit: any) => {
          landEdit.groundFish = this.edited_logbook.GF;
          landEdit.lastUpdate = new Date();
          return landEdit;
        }).then(() => res({success: true})).catch((err: any) => res({success: false, error: err}));
      })
    });
  }


  static editExistingHMSLanding(logbookId: string) {
    return new Promise<StorageResult>((res, rej) => {
      this.user_storage.get(logbookId).then((geEditDoc: any) => {
        this.edited_logbook.HMS = new HMSDelivery();
        this.edited_logbook.HMS.setValues(geEditDoc.landing);
        this.user_storage.upsert('logbook_edited', (landEdit: any) => {
          landEdit.HMS = this.edited_logbook.HMS;
          landEdit.lastUpdate = new Date();
          return landEdit;
        }).then(() => res({success: true})).catch((err: any) => res({success: false, error: err}));
      })
    });
  }


  static editExistingTrawlLanding(logbookId: string) {
    return new Promise<StorageResult>((res, rej) => {
      this.user_storage.get(logbookId).then((geEditDoc: any) => {
        this.edited_trawlLogbook = new TrawlDelivery();
        this.edited_trawlLogbook.setValues(geEditDoc.landing);
        this.user_storage.upsert('trawl_logbook_edited', (doc: any) => {
          doc.trawlDelivery = this.edited_trawlLogbook;
          doc.lastUpdate = new Date();
          return doc;
        }).then(() => res({success: true})).catch((err: any) => res({success: false, error: err}));
      })
    });
  }


  /**
   * The currently edited landing must be wiped
   */
  static discardLogbookEdits(fishery: 'default' | 'trawl' = 'default') {
    const docId = this.getDocId('logbook_edited', fishery);

    this.user_storage.upsert(docId, (ge: any) => {
      if (fishery === 'trawl') {
        this.edited_trawlLogbook = new TrawlDelivery();
        ge.trawlDelivery = new TrawlDelivery();
      } else {
        this.edited_logbook = { GF: new GroundfishDelivery(), HMS: new HMSDelivery() };
        ge.groundFish = new GroundfishDelivery();
        ge.HMS = new HMSDelivery();
      }
      ge.lastUpdate = new Date();
      return ge;
    });
  }


  /**
   * Update the user profile stored in local_storage and user_storage and update the user stored in login_status document.
   * Updated user info is taken from the current_user variable hence any updates to be propogated must be done to that variable first. 
   * @param userDBOpen Has the user_storage variable been initialized as a PouchDB instance? If not then can't update user_storage user doc
   * @param writeTokens Should the token static variables stored in this class be overwritten with the tokens stored in the current_user variable?
   * @returns Promise resolving on completion of all database operations
   */
  static updateUser(userDBOpen: boolean, writeTokens: boolean) {
    return new Promise<boolean>((resolve, reject) => {
      Promise.all(
        [
          new Promise<boolean>((res, rej) => {
            this.local_storage.get("user-" + this.current_user.userName.toLowerCase()).then((lp: any) => {
              lp.userInfo = { ...this.current_user };
              if (writeTokens) {
                console.log("Writing tokens");
                if (this.current_user.accessToken != "") this.accessToken = this.current_user.accessToken;
                if (this.current_user.refreshToken != "") this.refreshToken = this.current_user.refreshToken;
              }

              this.local_storage.get("login_status").then((doc: any) => {
                doc.user = { ...this.current_user };
                if (this.accessToken != "") doc.user.accessToken = this.accessToken;
                if (this.refreshToken != "") doc.user.refreshToken = this.refreshToken;
                this.local_storage.bulkDocs([lp, doc]).then((result: any) => {
                  res(true);
                }).catch((err: any) => res(false));
              })
            }).catch((err: any) => {
              this.insights.trackException(err);
              if (err.name == 'not_found') {
                res(false);
              }
            });
          }),

          new Promise<boolean>((res, rej) => {
            if (!userDBOpen) res(true);
            else {
              this.user_storage.get("user_doc").then((lp: any) => {
                lp.userInfo = { ...this.current_user };
                lp.userInfo.accessToken = "";
                lp.userInfo.refreshToken = "";
                //if (!writeTokens) lp.lastUpdate = new Date();
                this.user_storage.put(lp).then((_: any) => {
                  res(true);
                }).catch((err: any) => res(false));
              }).catch((err: any) => {
                console.log(err)
                this.insights.trackException(err);
                if (err.name == 'not_found') {
                  res(false);
                }
              })
            }
          })

        ]).then(_ => resolve(true));

    });

  }


  /**
   * Change the password for the user document in local_storage.
   * On login, the same update is made to the user_doc in user_storage.
   * @param username Username
   * @param newPassword The new password
   * @returns Promise resolving on completion of all database operations
   */
  static updateUserPassword(username: string, newPassword: string) {
    return new Promise((res, rej) => {
      this.local_storage.get("user-" + username.toLowerCase()).then((lp: any) => {
        lp.pwHash = encoders.toBase64(new Whirlpool().getHash(newPassword).toString());
        this.local_storage.put(lp).then(res(true)).catch(res(false));
      }).catch((err: any) => {
        if (err.name == 'not_found') {
          console.log('Could not find user, creating placeholder')
          this.local_storage.put(
            {
              "_id": "user-" + username.toLowerCase(),
              "userInfo": null,
              "pwHash": encoders.toBase64(new Whirlpool().getHash(newPassword).toString())
            }
          ).then((result: any) => {
            res(true);
          }).catch((err: any) => {
            console.log("Error on store user:");
            console.log(err);
            res(false);
          });
        }
      })
    });
  }


  /**
   * Check if the Groundfish only to combined HMS/Groundfish migration occurred. Fairly substantial data model changes required to accommodate both fisheries. 
   * @returns Promise resolving on completion of all database operations
   */
  static checkForMigration() {
    return new Promise<boolean>((rs, rj) => {
      LocalStorageService.user_storage = new PouchDB('user-storage-' + LocalStorageService.current_user.userName, { auto_compaction: true });
      this.user_storage.get('hms_migration').then((doc: any) => {
        rs(doc.complete);
      }).catch((err: any) => {
        if (err.name == "not_found") {
          console.log("%c HMS migration required!", "color: charcoal; font-weight: bold; background-color: coral;");
          this.user_storage.put({
            '_id': 'hms_migration',
            'complete': false
          }).then(() => { rs(false) })
        }
      })
    });
  }


  /**
   * With CouchDB server side and PouchDB client side, all data in the user_storage database is replicated with all changes to the CouchDB instance.
   * Equivalently, all data stored in the CouchDB instance is pulled down onto the device. Storage must be reopened in order to see any pulled down changes.
   * Conflict resolution is done via the ConflictResolverService.
   * @param reopenStorage Should the openLocalStorage method be run? That is, refetch all user data?
   */
  static syncUserDatabase(reopenStorage: boolean = false) {
    console.log('Performing sync...');
    if (this.sync_in_progress) {
      console.log('Sync already in progress...');
      return;
    }

    
    this.sync_promise = new Promise<boolean>((rs, rj) => {
      this.sync_in_progress = true;
      this.sync_in_progress$.next(this.sync_in_progress);
      this.reopenStorage().finally(() => {
        //Replicate all data from the couch down onto the app
        this.user_storage.replicate.from(this.current_user.couchDbConnection, {batch_size: 75, timeout: 8000}).on('complete', () => {
          console.log('Replicated from origin');

          //Conflicts are expected, run the auto-resolver
          ConflictResolverService.ResolveConflicts(this.user_storage).then((num_conflicts: number) => {
            console.log('Resolved conflicts on ' + num_conflicts + ' documents');

            //Updates to local user_doc and record time
            this.user_storage.allDocs({
              keys: ['user_doc', 'trip_pending', 'trawl_trip'],
              include_docs: true
            }).then((docs: any) => {
              let userDoc = docs.rows[0].doc;
              let tripDoc = docs.rows[1].doc;
              let trawlTripDoc = docs.rows[2].doc;

              tripDoc.lastSyncFromServer = new Date();
              this.user_trip.setValues(tripDoc.trip);
              this.user_trawlTrip.setValues(trawlTripDoc.trip);

              //Antecedent to conflict resolution, remove all sets from trip part of a completed submission
              this.user_trip.sets = this.user_trip.sets.filter(set => !this.received_logbooks.some(rl => rl.sets.some(rlSet => rlSet.fishingEventID == set.fishingEventID)));
              this.user_trawlTrip.sets = this.user_trawlTrip.sets.filter(set => !this.received_trawlLogbooks.some(rl => rl.sets.some(rlSet => rlSet.fishingEventID == set.fishingEventID)));
              
              this.current_user = OperatorUser.transformAnonymous(userDoc.userInfo);
              this.current_user.accessToken = this.accessToken;
              this.current_user.refreshToken = this.refreshToken;
              
              this.last_sync = new Date();
              this.last_sync$.next(this.last_sync);
              
              this.updateUser(true, false).then(() => {
                console.log('Replicated to origin');
                tripDoc.lastSyncToServer = new Date();
                tripDoc.trip = this.user_trip;
                this.user_storage.put(tripDoc).then(() => {
                  this.user_storage.replicate.to(this.current_user.couchDbConnection, {batch_size: 75, timeout: 8000}).on('complete', () => {
                    this.user_storage.upsert('_local/last_sync', (doc: any) => {
                      doc.last_sync = this.last_sync;
                      return doc;
                    }).then(() => {
                      if (reopenStorage) {
                        this.openLocalStorage();
                        this.sync_in_progress = false;
                        this.sync_in_progress$.next(this.sync_in_progress);
                        rs(true);
                      } else {
                        this.sync_in_progress = false;
                        this.sync_in_progress$.next(this.sync_in_progress);
                        rs(true);
                      }
                    });
                  }).on('error', (err: any) => {
                    this.insights.trackException(err);
                  });
                })
              });
            });
          }).catch((err: any) => {
            this.insights.trackException(err);
            console.log(err);
            if (reopenStorage) {
              this.openLocalStorage();
              this.sync_in_progress = false;
              this.sync_in_progress$.next(this.sync_in_progress);
              rs(false);
            } else {
              this.sync_in_progress = false;
              this.sync_in_progress$.next(this.sync_in_progress);
              rs(false);
            }
          })
        }).on('error', (err: any) => {
          console.log("Sync failed");
          this.insights.trackException(err);
          console.log(err);
          if (reopenStorage) {
            this.openLocalStorage();
            this.sync_in_progress = false;
            this.sync_in_progress$.next(this.sync_in_progress);
            rs(false);
          } else {
            this.sync_in_progress = false;
            this.sync_in_progress$.next(this.sync_in_progress);
            rs(false);
          }
        });
      });
    });
    
  }


  /**
   * Migration to a new data model for a combination of HMS and Groudfish
   * @returns Promise resolving on completion of all database operations
   */
  static migrateToHMSCombined() {
    console.log("Attempting migration to HMS...")
    this.insights.trackEvent('HMS Migration', 'Attempting an HMS migration');
    return new Promise<boolean>((rs0, rj0) => {

      this.user_storage.allDocs({
        keys: ['user_sets_pending', 'landing_pending', 'landing_edited'],
        include_docs: true
      }).then((docs: any) => {
        console.log("Docs before migration")
        console.log(docs)

        Promise.all([
          //Gear docs, delivery buyers, permit numbers can stay as they are

          //Current/pending landing - Create a trip with the old landing transformed to a GroundfishDelivery under it
          //Attach all sets from the pending sets list under this trip
          //Delete the old pending sets and pending landing doc
          new Promise<boolean>((rs0, rj0) => {
            if (docs.rows[1].error == 'not_found') {
              console.log("Pending landing not found");
              rs0(true);
            } else {
              let t = new Trip();
              t.groundfishDelivery = GroundfishDelivery.transformAnonymous(docs.rows[1].doc.landing);
              let deleteSets: any[] = [];

              this.user_storage.allDocs({
                keys: docs.rows[0].doc.list,
                include_docs: true
              }).then((sets: any) => {
                console.log("All sets pre-migration")
                console.log(sets)
                sets.rows.forEach((st: any) => {
                  let doc = st.doc;
                  let fe = new FishingEvent(doc.StringSetNumber, "Groundfish");
                  fe.fishingEventID = doc._id;
                  fe.landingID = doc.LandingID;
                  fe.gear = new Gear();
                  fe.gear.gearID = doc.GearID;
                  fe.startDate = doc.FishingStartDateTime;
                  fe.startLat = Coordinate.transformAnon(doc.FishingStartLatitude);
                  fe.startLon = Coordinate.transformAnon(doc.FishingStartLongitude);
                  fe.startDepth = doc.FishingStartDepth;
                  fe.depthOffBottom = doc.DepthOffBottom;
                  fe.endDate = doc.FishingEndDateTime;
                  fe.endLat = Coordinate.transformAnon(doc.FishingEndLatitude);
                  fe.endLon = Coordinate.transformAnon(doc.FishingEndLongitude);
                  fe.endDepth = doc.FishingEndDepth;
                  fe.gearPerSet = doc.GearPerSet;
                  fe.skateNum = doc.SkateNum;
                  fe.lostPotCount = doc.LostPotCount;
                  fe.catchItems = Catch.transformAnonymous(doc.CatchItems);
                  fe.startLatManualEntry = doc.sLatMan;
                  fe.startLonManualEntry = doc.sLonMan;
                  fe.endLatManualEntry = doc.eLatMan;
                  fe.endLonManualEntry = doc.eLonMan;
                  fe.baitType = doc.baitType;
                  fe.comments = doc.comments;
                  t.sets.push(fe);
                  doc._deleted = true
                  deleteSets.push(doc);
                });
                //Delete the pending sets doc and landing pending doc
                docs.rows[0].doc._deleted = true;
                docs.rows[1].doc._deleted = true;
                let curr_trip_doc = {
                  "_id": "trip_pending",
                  "trip": t,
                  "lastUpdate": new Date()
                };
                console.log(curr_trip_doc)
                this.user_storage.bulkDocs(deleteSets.concat([docs.rows[0].doc, docs.rows[1].doc, curr_trip_doc])).then(() => {
                  LocalStorageService.user_storage.get('trip_pending').then((doc: any) => console.log(doc));
                  rs0(true)
                });
              });
            }

          }),

          //Edited landing - This will correspond to one of the trips under the recieved logbooks, 
          //copy the groundfish delivery of the trip and apply edits
          //Delete the old landing edited doc, create trip edited doc
          new Promise<boolean>((rs1, rj1) => {
            if (docs.rows[2].error == 'not_found') rs1(true);
            else {
              let gf = GroundfishDelivery.transformAnonymous(docs.rows[2].doc.landing);
              let edited_trip_doc = {
                "_id": "logbook_edited",
                "groundFish": gf,
                "HMS": new HMSDelivery(),
                "lastUpdate": new Date()
              };
              docs.rows[2].doc._deleted = true;
              this.user_storage.bulkDocs([docs.rows[2].doc, edited_trip_doc]).then(() => rs1(true));
            }

          })
        ]).then(() => {
          console.log("Migration complete");
          this.user_storage.get('hms_migration').then((doc: any) => {
            doc.complete = true;
            this.user_storage.put(doc).then(() => rs0(true));
          });
        }).catch(err => {
          console.log(err);
          rs0(false);
        })

      }).catch((err: any) => {
        console.log(err);
      });

    });
  }


  // Helper method to pick Trawl docs vs. default docs
  private static getDocId(
    type: string,
    fishery: 'default' | 'trawl'
  ): string {
    if (fishery === 'trawl') {
      switch (type) {
        case 'trip': return 'trawl_trip';
        case 'logbook_edited': return 'trawl_logbook_edited';
        case 'landings_received': return 'trawl_landings_received';
        case 'permit_numbers': return 'trawl_permit_numbers';
        case 'delivery_buyers': return 'trawl_delivery_buyers';
        case 'trawl_drafts': return 'trawl_drafts';
        case 'mothership_names': return 'mothership_names';
        default: return '';
      }
    } else {
      switch (type) {
        case 'trip': return 'trip_pending';
        case 'logbook_edited': return 'logbook_edited';
        case 'landings_received': return 'user_landings_received';
        case 'permit_numbers': return 'permit_numbers';
        case 'mothership_names': return 'mothership_names';
        case 'delivery_buyers': return 'delivery_buyers';
        case 'hms_drafts': return 'hms_drafts';   
        default: return '';
      }
    }
  }

}